import * as React from "react";
import { NavLink } from "react-router-dom";

import { cn } from "@/lib/utils";
import {
  CalendarClock,
  CalendarDays,
  CircleUser,
  House,
  IdCard,
  ShieldCheck,
  UsersRound,
} from "lucide-react";

/**
 * The header's icon navigation. Uses Lucide's modern icon set (the app's icon
 * library) so every glyph is crisp and consistent. Each item carries an
 * {@code aria-label} (screen readers) plus a hover tooltip (sighted users) and a
 * themed colour from the app's emerald→cyan→violet palette.
 *
 * <p>On hover an icon lifts slightly and glows in its own colour (a coloured
 * {@code drop-shadow}) — a small, tasteful bit of life on an otherwise calm bar.</p>
 */

/** Hover tooltip shown under an icon. Purely decorative — the aria-label carries the real name. */
export function IconTooltip({ label }: { label: string }) {
  return (
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-foreground px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide text-background opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100"
    >
      {label}
    </span>
  );
}

const baseClass =
  "group relative inline-flex h-9 w-9 items-center justify-center rounded-xl transition-colors hover:bg-muted";

/**
 * Shared icon styling: sized, and on hover it lifts and glows in the current
 * text colour. `[filter:drop-shadow(...)]` with `currentColor` means the glow
 * always matches the icon's themed colour without a per-icon override.
 */
export const navIconClass =
  "h-5 w-5 transition-all duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-110 group-hover:[filter:drop-shadow(0_0_7px_currentColor)]";

/** A small brand-contrast (#9B1313) line under the active nav item — the current-page marker. */
function ActiveMark() {
  return (
    <span className="pointer-events-none absolute inset-x-2 bottom-0.5 h-0.5 rounded-full bg-brand-contrast" />
  );
}

/**
 * An internal or external icon link with a tooltip. Internal links use NavLink so the
 * current page shows a small brand-contrast underline (the active marker). "/" matches
 * exactly (end) so Home isn't marked active on every route.
 */
export function NavIconLink({
  to,
  label,
  color,
  external,
  children,
}: {
  to: string;
  label: string;
  color: string;
  external?: boolean;
  children: React.ReactNode;
}) {
  if (external) {
    return (
      <a href={to} target="_blank" rel="noreferrer" aria-label={label} className={`${baseClass} ${color}`}>
        {children}
        <IconTooltip label={label} />
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      end={to === "/"}
      aria-label={label}
      className={({ isActive }) => cn(baseClass, color, isActive && "bg-muted")}
    >
      {({ isActive }) => (
        <>
          {children}
          <IconTooltip label={label} />
          {isActive && <ActiveMark />}
        </>
      )}
    </NavLink>
  );
}

/** Home — house. */
export function HomeIcon() {
  return <House className={navIconClass} aria-hidden />;
}

/** Meetings — calendar. */
export function CalendarIcon() {
  return <CalendarDays className={navIconClass} aria-hidden />;
}

/** Booking pages — book a time slot. */
export function BookingIcon() {
  return <CalendarClock className={navIconClass} aria-hidden />;
}

/** Profile — you, in a circle. */
export function UserIcon() {
  return <CircleUser className={navIconClass} aria-hidden />;
}

/** My card — id / contact card. */
export function IdCardIcon() {
  return <IdCard className={navIconClass} aria-hidden />;
}

/** Connections — people (not a bell; that read as notifications). */
export function ConnectionsIcon() {
  return <UsersRound className={navIconClass} aria-hidden />;
}

/** Admin — shield with a check. */
export function ShieldIcon() {
  return <ShieldCheck className={navIconClass} aria-hidden />;
}
